import en from "./en";

export type Locale = "hr" | "en";

export const LOCALES: { id: Locale; label: string; name: string }[] = [
  { id: "hr", label: "HR", name: "Hrvatski" },
  { id: "en", label: "EN", name: "English" },
];

const STORAGE_KEY = "mes_locale";

const isLocale = (value: unknown): value is Locale => value === "hr" || value === "en";

const readStored = (): Locale => {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (isLocale(value)) return value;
  } catch {
    // Ignore storage errors (private mode, SSR).
  }
  return "hr";
};

let current: Locale = typeof window !== "undefined" ? readStored() : "hr";
const listeners = new Set<() => void>();

export const getLocale = (): Locale => current;

export const setLocale = (next: Locale): void => {
  if (next === current) return;
  current = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Ignore storage errors.
  }
  listeners.forEach((listener) => listener());
};

export const subscribeLocale = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** BCP 47 tag for dates and numbers. */
export const dateLocale = (): string => (current === "en" ? "en-GB" : "hr-HR");

/** localeCompare language. */
export const compareLocale = (): string => (current === "en" ? "en" : "hr");

export const interpolate = (
  template: string,
  params?: Record<string, string | number>
): string => {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, key: string) =>
    params[key] != null ? String(params[key]) : `{${key}}`
  );
};

/**
 * Translate a Croatian source string. Croatian is the identity locale;
 * English looks the string up in `en` and falls back to the source.
 */
export const t = (key: string, params?: Record<string, string | number>): string => {
  const template = current === "en" ? en[key] ?? key : key;
  return interpolate(template, params);
};

export const formatMesDate = (
  value?: string | Date | null,
  empty = "—"
): string => {
  if (!value) return empty;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return empty;
  return date.toLocaleDateString(dateLocale());
};

export const formatMesDateTime = (
  value?: string | Date | null,
  empty = "—"
): string => {
  if (!value) return empty;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return empty;
  return date.toLocaleString(dateLocale(), { dateStyle: "short", timeStyle: "short" });
};

export const formatMesNumber = (
  value: number,
  options?: Intl.NumberFormatOptions
): string => value.toLocaleString(dateLocale(), options);
